/**
 * Firebase Token Service
 * Handles Firebase token exchange using WebView access token
 */

import { FirebaseTokenRequest, FirebaseTokenResponse } from '../types';

export class FirebaseTokenService {
  private static instance: FirebaseTokenService;
  private firebaseToken: string | null = null;
  private tokenExpiry: number | null = null;

  private constructor() {}

  static getInstance(): FirebaseTokenService {
    if (!FirebaseTokenService.instance) {
      FirebaseTokenService.instance = new FirebaseTokenService();
    }
    return FirebaseTokenService.instance;
  }

  /**
   * Exchange WebView access token for Firebase token
   */
  async exchangeToken(
    firebaseAuthUrl: string,
    accessToken: string,
    tenantId: string
  ): Promise<string> {
    try {
      // Check if we have a valid cached token
      if (this.firebaseToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
        return this.firebaseToken;
      }

      // Make request to Firebase auth endpoint
      const response = await fetch(firebaseAuthUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          accessToken,
          tenantId,
        } as FirebaseTokenRequest),
      });

      if (!response.ok) {
        throw new Error(`Firebase token exchange failed: ${response.status} ${response.statusText}`);
      }

      const data: FirebaseTokenResponse = await response.json();

      if (!data.success || !data.result) {
        throw new Error(data.error?.message || 'Failed to get Firebase token');
      }

      // Cache the token
      this.firebaseToken = data.result.token;
      this.tokenExpiry = Date.now() + (data.result.expiresIn * 1000);

      return data.result.token;
    } catch (error) {
      console.error('Firebase token exchange error:', error);
      throw error;
    }
  }

  /**
   * Get current Firebase token
   */
  getCurrentToken(): string | null {
    if (this.firebaseToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.firebaseToken;
    }
    return null;
  }

  /**
   * Clear cached token
   */
  clearToken(): void {
    this.firebaseToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    return !this.tokenExpiry || Date.now() >= this.tokenExpiry;
  }
}